import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { MissingEntry } from "@/types/models";

// One unified "daily logging is behind" nudge — every dashboard and P&L number is
// only as current as the logging behind it. Merges the freshness headline (how
// many complete days behind) with the actionable per-flock/date checklist.
//
// `daysBehind` EXCLUDES today (the in-progress day) — a farm that logged today or
// yesterday is 0 days behind and, with no missing entries, this renders nothing.
export default function DailyLoggingCard({
  daysBehind,
  lastLogDate,
  entries,
  canLog, // viewers are read-only — show the warning but not a dead-end CTA
}: {
  daysBehind: number | null;
  lastLogDate: string | null;
  entries: MissingEntry[];
  canLog: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, MissingEntry[]>();
    for (const e of entries) {
      if (!map.has(e.date_label)) map.set(e.date_label, []);
      map.get(e.date_label)!.push(e);
    }
    return map;
  }, [entries]);

  const behind = daysBehind ?? 0;
  const hasDetail = entries.length > 0;
  // NOTHING HAS EVER BEEN LOGGED — a different situation from being behind, and
  // the one a farm is in after an import, a reset, or before it has started.
  // `days_behind` is null here too, so `behind` is 0 and every threshold below
  // reads it as "up to date". Without this the card said "Some daily entries
  // are missing" over "12 missing entries across the last 3 days" to a farm
  // whose entire history is absent — describing a three-day gap where there is
  // no book at all.
  const neverLogged = lastLogDate == null;

  // Show when nothing has ever been logged, for a sustained lapse (≥3 complete
  // days), or whenever specific recent entries are missing.
  if (!neverLogged && !hasDetail && behind < 3) return null;

  const severe = neverLogged || behind >= 14;
  const tone = severe
    ? "border-error-200 bg-error-50 text-error-800 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-300"
    : "border-warning-200 bg-warning-50 text-warning-800 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-warning-300";

  const headline = neverLogged
    ? "No production has ever been logged for this farm."
    : behind >= 3
      ? `No production logged in ${behind} days (since ${lastLogDate}).`
      : "Some daily entries are missing.";

  const subline = neverLogged
    ? "Your flocks and bird counts are recorded, but no eggs, feed or mortality have been entered — so every production number, chart and P&L figure reads zero."
    : "Every dashboard and P&L number is only as current as your daily logging.";

  const labels = Array.from(grouped.keys());

  return (
    <div className={`mb-6 rounded-2xl border ${tone}`} role="status">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3">
        <div>
          <p className="text-sm font-semibold">{headline}</p>
          <p className="mt-0.5 text-xs opacity-90">{subline}</p>
        </div>
        {canLog && (
          <Link
            to="/daily-report"
            className="shrink-0 rounded-lg bg-white/70 px-3 py-2 text-sm font-medium shadow-theme-xs hover:bg-white dark:bg-white/10 dark:hover:bg-white/20"
          >
            Log production &rarr;
          </Link>
        )}
      </div>

      {hasDetail && (
        <div className="border-t border-current/15 px-5 pb-3 pt-2">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex w-full items-center justify-between text-left"
            aria-expanded={expanded}
          >
            <span className="text-xs font-medium opacity-90">
              {/* The window is only ever the last 3 days. Saying so to a farm
                  with no history at all understates it as a three-day gap, so
                  the phrasing changes rather than the number. */}
              {neverLogged
                ? `${entries.length} ${entries.length === 1 ? "entry" : "entries"} missing in the last 3 days alone`
                : `${entries.length} missing ${entries.length === 1 ? "entry" : "entries"} across the last 3 days`}
            </span>
            <svg
              className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expanded && (
            <div className="mt-3 space-y-3">
              {labels.map((label) => (
                <div key={label}>
                  <p className="mb-1 text-xs font-medium uppercase opacity-75">{label}</p>
                  <ul className="space-y-1">
                    {grouped.get(label)!.map((entry) => (
                      <li key={`${entry.flock_id}-${entry.date}`} className="text-sm opacity-90">
                        <span className="font-medium">{entry.flock_name}</span>
                        {" — "}
                        {entry.missing.map((type, i) => (
                          <span key={type}>
                            {i > 0 && ", "}
                            <Link
                              to={type === "Feed" ? "/feed" : "/eggs"}
                              className="underline hover:opacity-100"
                            >
                              {type}
                            </Link>
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
